import {Component, OnDestroy, OnInit} from '@angular/core';
import {StatistiqueEgalimDTO} from "../../core/dtos/egalim/statistique-egalim-dto";
import {SelectionChangedEvent, ToolbarPreparingEvent} from "devextreme/ui/data_grid";
import {StatistiquesEGAlimService} from "../../core/services/egalim/statistiques-egalim.service";
import {IconThemeEnum} from "../../shared/ui/icon/icon-theme-enum";
import {ButtonThemeEnum} from "../../shared/ui/button/button-theme-enum";
import {MaCantineConnectionDTO} from "../../core/dtos/egalim/ma-cantine-connection-dto";
import {Subscription} from "rxjs";
import {UtilsService} from "../../core/utils/utils.service";
import {ToastService} from "../../core/services/technique/toast.service";
import {MSG_KEY, MSG_SEVERITY} from "../../core/constants";
import {GraphQLService} from "../../core/services/technique/graphql.service";
import {saveAs} from "file-saver";
import {MailHistoDTO} from "../../core/dtos/mail-histo-dto";
import {EmailContextEnum} from "../../core/dtos/email/email-context-enum";

@Component({
    selector: 'yo-statistiques-egalim',
    templateUrl: './statistiques-egalim.component.html',
    styleUrls: ['./statistiques-egalim.component.scss'],
    standalone: false
})
export class StatistiquesEgalimComponent implements OnInit, OnDestroy {

  public statistiques: StatistiqueEgalimDTO[] = [];
  public selectedRows: number[] = [];

  public maCantineConnection: MaCantineConnectionDTO;

  private subData: Subscription;
  private subMaCantineConnection: Subscription;
  private subOnMaCantineConnection: Subscription;
  private subOnStatistiqueEGAlimGenerated: Subscription;

  public readonly ButtonThemeEnum = ButtonThemeEnum;
  public readonly EmailContextEnum = EmailContextEnum;

  constructor(private statistiquesEgalimSvc: StatistiquesEGAlimService,
              private utilsSvc: UtilsService,
              private toastSvc: ToastService,
              private graphQLSvc: GraphQLService) {
  }

  ngOnInit(): void {
    this.initData();
    this.initMaCantineConnection();
    this.initSubOnMaCantineConnection();
    this.initSubOnStatistiqueEGAlimGenerated();
  }

  ngOnDestroy(): void {
    this.utilsSvc.unsubscribe(this.subData);
    this.utilsSvc.unsubscribe(this.subMaCantineConnection);
    this.utilsSvc.unsubscribe(this.subOnMaCantineConnection);
    this.utilsSvc.unsubscribe(this.subOnStatistiqueEGAlimGenerated);
  }

  initMaCantineConnection = () => {
    this.subMaCantineConnection = this.statistiquesEgalimSvc.getMaCantineConnection().subscribe(result => {
      if (result && result.one)
        this.maCantineConnection = result.one;
    });
  };

  initSubOnMaCantineConnection = () => {
    this.subOnMaCantineConnection = this.statistiquesEgalimSvc.onMaCantineCredentialsUpdate$.subscribe(connection => {
      this.maCantineConnection = connection;
    })
  }

  initSubOnStatistiqueEGAlimGenerated = () => {
    this.subOnStatistiqueEGAlimGenerated = this.statistiquesEgalimSvc.onStatistiqueEGAlimGenerated$.subscribe(statistique => {
      const index = this.statistiques.map(s => s.id).indexOf(statistique.id)
      if (index >= 0)
        this.statistiques.splice(index, 1, statistique);
      else
        this.statistiques.push(statistique);
    })
  }

  private initData() {
    this.subData = this.graphQLSvc.sendQuery(`
    {
        allStatistiqueEGAlim {
            id,
            site {
                id,
                libelle
            },
            annee,
            dateGeneration,
            dateDiagnosticSent,
            pointTeledeclaration {
                id,
                libelle,
                siret
            },
            isDiagnosticSent,
            isConforme,
            emailHisto {
              statut
            }
        }
    }
    `).subscribe(result => {
      if (result) {
        this.statistiques = result.allStatistiqueEGAlim;
      }
    });
  }

  onToolbarPreparing($event: ToolbarPreparingEvent) {
    $event.toolbarOptions.items.unshift(
      {
        location: "before",
        template: "beforeTemplate"
      },
      {
        location: "after",
        template: "afterTemplate"
      }
    );
  }

  openDialogGeneration() {
    this.statistiquesEgalimSvc.openCreationDialog();
  }

  getDiagnosticTheme(data: StatistiqueEgalimDTO) {
    if (data.isDiagnosticSent) {
      if (data.dateDiagnosticSent != null) {
        if (this.isDiagnosticOutdated(data.dateGeneration, data.dateDiagnosticSent))
          return IconThemeEnum.WARNING_INNER
        else
          return IconThemeEnum.SUCCESS_INNER;

      } else
        return IconThemeEnum.WARNING_INNER
    }
    return IconThemeEnum.ERROR_INNER;
  }

  getDiagnosticIcon(data: StatistiqueEgalimDTO) {
    if (data.isDiagnosticSent
      && data.dateDiagnosticSent != null
      && !this.isDiagnosticOutdated(data.dateGeneration, data.dateDiagnosticSent))
      return 'check-circle';
    return 'times-circle';
  }

  getConformiteTheme(isConforme: boolean) {
    if (isConforme)
      return IconThemeEnum.SUCCESS_INNER;
    return IconThemeEnum.ERROR_INNER;
  }

  getConformiteIcon(isConforme: boolean) {
    if (isConforme)
      return 'thumbs-up';
    return 'thumbs-down';
  }

  getConformiteTitle(isConforme: boolean) {
    if (isConforme)
      return 'Conforme';
    return 'Non conforme';
  }

  getConnectionButtonTheme() {
    if (this.maCantineConnection && this.maCantineConnection.clientId) {
      if (this.maCantineConnection.isConnected)
        return ButtonThemeEnum.SUCCESS_INNER

      return ButtonThemeEnum.WARNING;
    }
    return ButtonThemeEnum.ERROR;
  }

  openMaCantineConnectionDialog = () => {
    this.statistiquesEgalimSvc.openMaCantineConnectionDialog(this.maCantineConnection);
  };

  sendDiagnostics() {
    this.statistiquesEgalimSvc.teledeclare(this.selectedRows).subscribe(result => {
      if (result) {
        result.one.value.forEach((stat) => {
          this.statistiquesEgalimSvc.onStatistiqueEGAlimGenerated(stat);
        })

        if (result.one.errors.length > 0) {
          this.statistiquesEgalimSvc.openErrorDialog(result.one.errors);
        } else
          this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, "Diagnostics envoyés avec succès")
      }
    })
  }

  openDiagnosticDialog = (statistique: StatistiqueEgalimDTO) => {
    this.statistiquesEgalimSvc.openDetailsDialog(statistique.id);
  }

  openConformiteDialog = (statistique: StatistiqueEgalimDTO) => {
    this.statistiquesEgalimSvc.openConformiteDialog(statistique)
  }

  onSelectLine($event: SelectionChangedEvent) {
    this.selectedRows = $event.selectedRowKeys;
  }

  exportDiagnostics() {
    this.statistiquesEgalimSvc.export(this.selectedRows).subscribe(result => {
      const blob = result as Blob;
      saveAs(blob, "diagnostic");
    });
  }

  getDiagnosticTitle(data: StatistiqueEgalimDTO) {
    if (data.isDiagnosticSent) {
      if (data.dateDiagnosticSent != null) {
        if (this.isDiagnosticOutdated(data.dateGeneration, data.dateDiagnosticSent))
          return "Ancienne version envoyée le " + this.utilsSvc.formatDateTime(data.dateDiagnosticSent)
        else
          return "Envoyé le " + this.utilsSvc.formatDateTime(data.dateDiagnosticSent);
      } else
        return "Ancienne version envoyée";
    }
    return "Non envoyé";
  }

  private isDiagnosticOutdated(dateGeneration: Date, dateDiagnosticSent: Date) {
    return dateGeneration > dateDiagnosticSent;
  }

  sendMail() {
    this.statistiquesEgalimSvc.sendByEmail(this.selectedRows).subscribe(result => {
      if (result) {
        if (result.one.errors.length > 0)
          this.statistiquesEgalimSvc.openErrorDialog(result.one.errors);
        else
          this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, "Diagnostics envoyés par emails avec succès")

        result.one.value.forEach((stat) => {
          this.statistiquesEgalimSvc.onStatistiqueEGAlimGenerated(stat);
        })
      }
    });
  }

  getLastEmail(emailHisto: MailHistoDTO[]) {
    if (emailHisto)
      return emailHisto[emailHisto.length - 1];
    return null;
  }

  onMailHistoRetrieved(mailHisto: MailHistoDTO, id: number) {
    const stat = this.statistiques.filter(s => s.id == id)[0];
    const index = stat.emailHisto.map(s => s.id).indexOf(mailHisto.id);

    if (index >= 0)
      stat.emailHisto.splice(index, 1, mailHisto);
    else
      stat.emailHisto.push(mailHisto);
  }

  isAtLeastOneMail() {
    for (const s of this.statistiques) {
      if (s?.emailHisto?.length > 0)
        return true;
    }
    return false;
  }
}
